import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, AlertTriangle, X, Search, Save, RefreshCw, Download, ZoomIn, ZoomOut, RotateCcw, Grid3X3, FileSpreadsheet } from 'lucide-react';
import ReactPDFViewer from './ReactPDFViewer';
import OCRLabelingModal from './OCRLabelingModal';
import BatchAUIGrid from './BatchAUIGrid';
import * as XLSX from 'xlsx';

const BatchValidationGrid = ({ fileList, API_BASE_URL, templates, onClose, onRefresh, selectedTemplateId: externalTemplateId, onRowSelect }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null); // For mapping modal
  const [selectedVehicleId, setSelectedVehicleId] = useState(null); // For PDF viewer
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [similarMatches, setSimilarMatches] = useState([]);
  const [searchingSimilarity, setSearchingSimilarity] = useState(false);
  const [showOnlyMismatch, setShowOnlyMismatch] = useState(false); // 100% 미만만 표시
  const [showMergedView, setShowMergedView] = useState(true); // 📄 머지 결과 보기 (true) vs 원본 페이지별 (false)
  const [internalTemplateId, setInternalTemplateId] = useState(''); // 내부 템플릿 ID (자동 선택용)

  // ⭐ 외부에서 전달된 템플릿 ID 우선, 없으면 내부 자동 선택 ID 사용
  const selectedTemplateId = externalTemplateId || internalTemplateId;
  const [bottomHeight, setBottomHeight] = useState(400); // 하단 패널 높이 (기본값 400px)
  const [scale, setScale] = useState(1.5); // PDF 줌 스케일 (기본 1.5)
  // 🚀 [Upstage Benchmark] View Modal State
  const [viewDataModal, setViewDataModal] = useState(null);
  const [showLabels, setShowLabels] = useState(true); // 🎯 Labels 표시 토글
  const [highlightedFieldKey, setHighlightedFieldKey] = useState(null); // ⭐ 필드 클릭 시 하이라이트할 필드값
  const [useAUIGrid, setUseAUIGrid] = useState(true); // 🆕 AUIGrid 사용 여부 토글 (기본: true)

  // ⭐ 필드 클릭 핸들러 - 해당 bbox를 문서에서 하이라이트
  const handleFieldClick = (fieldKey, fieldValue) => {
    console.log('🎯 [BatchGrid] Field Click:', fieldKey, '=', fieldValue);
    // 필드 값으로 bbox 매칭 (OCR 텍스트와 비교)
    setHighlightedFieldKey(fieldValue || fieldKey);
    // 3초 후 하이라이트 해제
    setTimeout(() => setHighlightedFieldKey(null), 3000);
  };

  // 줌 핸들러
  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const handleZoomReset = () => setScale(1.5);

  // 리사이징 핸들러 (🚀 60fps로 부드럽게)
  const resizeAnimationFrameRef = useRef(null);
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startHeight = bottomHeight;
    const containerElement = e.target.parentElement?.parentElement;

    // 🚀 드래그 중 스타일 설정
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';
    document.body.style.pointerEvents = 'none';
    e.target.style.pointerEvents = 'auto';

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();

      // 이전 프레임 취소
      if (resizeAnimationFrameRef.current) {
        cancelAnimationFrame(resizeAnimationFrameRef.current);
      }

      // 🚀 requestAnimationFrame으로 60fps 업데이트
      resizeAnimationFrameRef.current = requestAnimationFrame(() => {
        const delta = startY - moveEvent.clientY;
        const newHeight = Math.max(100, Math.min(startHeight + delta, window.innerHeight - 200));
        setBottomHeight(newHeight);
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // 🚀 스타일 복원
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.body.style.pointerEvents = '';

      // 애니메이션 프레임 취소
      if (resizeAnimationFrameRef.current) {
        cancelAnimationFrame(resizeAnimationFrameRef.current);
        resizeAnimationFrameRef.current = null;
      }
    };

    // 🚀 passive: false로 즉시 반응
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp, { passive: false });
  };

  // 엑셀 다운로드 - 🔧 Single + Table Fields + Computed Fields 지원
  const handleExcelDownload = () => {
    const selectedTemplate = selectedTemplateId ? templates[selectedTemplateId] : null;
    const singleFields = selectedTemplate?.singleFields || selectedTemplate?.fields || [];
    const tableFields = selectedTemplate?.tableFields || [];
    const computedFields = selectedTemplate?.computedFields || [];

    // 합성 필드 계산 함수 (대소문자 무시 매칭)
    const evaluateComputedField = (formula, sourceData) => {
      if (!formula) return '';
      let result = formula;

      // formula에서 {placeholder} 형태를 모두 찾음
      const placeholders = formula.match(/\{([^}]+)\}/g) || [];

      placeholders.forEach(placeholder => {
        const keyInFormula = placeholder.slice(1, -1); // {key} -> key

        // sourceData에서 대소문자 무시하고 키 찾기
        const matchingKey = Object.keys(sourceData).find(
          k => k.toLowerCase() === keyInFormula.toLowerCase()
        );

        if (matchingKey && sourceData[matchingKey]) {
          result = result.replace(placeholder, sourceData[matchingKey]);
        } else {
          result = result.replace(placeholder, ''); // 매칭 안되면 빈 문자열
        }
      });

      return result.trim();
    };

    const wb = XLSX.utils.book_new();

    // 🔧 테이블 필드가 있으면 펼치기 로직 적용
    if (tableFields.length > 0) {
      // 각 테이블별로 별도 시트 생성
      tableFields.forEach((table, tableIndex) => {
        const flattenedData = [];

        (showOnlyMismatch ? vehicles.filter(v => v.mappingStatus !== 'exact') : vehicles).forEach(v => {
          // 단일 필드 데이터 수집 (파일명, 페이지)
          const baseSingleData = {
            '파일명': v.fileName,
            '페이지': v.pageNumber,
          };

          // 최상위 단일 필드 추가
          singleFields.forEach(field => {
            const fieldKey = typeof field === 'string' ? field : field.key;
            baseSingleData[fieldKey] = v.current[fieldKey] || '-';
          });

          // 테이블 데이터 가져오기 (🔧 FIX: items 폴백 추가)
          let tableData = v.current[table.tableName] || [];
          if (tableData.length === 0 && v.current.items) {
            tableData = v.current.items;
          }

          if (Array.isArray(tableData) && tableData.length > 0) {
            // 테이블 행마다 단일 필드 반복
            tableData.forEach((row, rowIdx) => {
              const flatRow = { ...baseSingleData, '행번호': rowIdx + 1 };

              // 🔧 합성 필드는 각 ROW 데이터로 계산 (MODEL, Year 등은 row에 있음)
              computedFields.forEach(cf => {
                const displayName = cf.displayName || cf.key;
                flatRow[displayName] = evaluateComputedField(cf.formula, row);
              });

              // 테이블 컬럼 추가
              table.columns.forEach(col => {
                if (col.type === 'computed' && col.formula) {
                  // 🔧 테이블 합성 필드 계산
                  let computed = col.formula;
                  const pattern = /\{([^}]+)\}/g;
                  let hasReplacement = false;

                  computed = computed.replace(pattern, (_, key) => {
                    hasReplacement = true;
                    const valKey = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
                    let val = valKey ? row[valKey] : '';
                    if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) {
                      return val.replace(/,/g, '');
                    }
                    return val || 0;
                  });

                  if (hasReplacement && /^[\d+\-*/.()\s]+$/.test(computed)) {
                    try {
                      // eslint-disable-next-line no-eval
                      flatRow[col.key] = String(eval(computed));
                    } catch (e) {
                      flatRow[col.key] = computed;
                    }
                  } else {
                    // 문자열 결합 (재치환)
                    let stringComputed = col.formula;
                    stringComputed = stringComputed.replace(pattern, (_, key) => {
                      const valKey = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
                      return valKey ? row[valKey] : '';
                    });
                    flatRow[col.key] = stringComputed;
                  }
                } else {
                  flatRow[col.key] = row[col.key] || '-';
                }
              });
              flattenedData.push(flatRow);
            });
          } else {
            // 테이블 데이터 없으면 단일 필드만 출력
            const flatRow = { ...baseSingleData, '행번호': '-' };
            computedFields.forEach(cf => {
              const displayName = cf.displayName || cf.key;
              flatRow[displayName] = '-';
            });
            table.columns.forEach(col => {
              flatRow[col.key] = '-';
            });
            flattenedData.push(flatRow);
          }
        });

        const ws = XLSX.utils.json_to_sheet(flattenedData);
        const sheetName = table.tableName.substring(0, 31); // Excel 시트 이름 제한
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
    } else {
      // 기존 로직: 테이블 필드 없음
      const data = (showOnlyMismatch ? vehicles.filter(v => v.mappingStatus !== 'exact') : vehicles).map(v => {
        const baseData = {
          '파일명': v.fileName,
          '페이지': v.pageNumber,
        };

        // 합성 필드 추가
        computedFields.forEach(cf => {
          const displayName = cf.displayName || cf.key;
          baseData[displayName] = evaluateComputedField(cf.formula, v.current);
        });

        if (singleFields.length > 0) {
          singleFields.forEach(field => {
            const fieldKey = typeof field === 'string' ? field : field.key;
            baseData[fieldKey] = v.current[fieldKey] || '-';
          });
        } else {
          // 기본 템플릿 (기존 로직)
          Object.assign(baseData, {
            '문서종류': v.documentType || '-',
            '차량명(인식)': v.current.model || '-',
            '매핑상태': v.mappingStatus === 'exact' ? '100% 일치' : v.mappingStatus === 'similar' ? `유사(${Math.round((v.similarity || 0) * 100)}%)` : v.mappingStatus === 'not_found' ? '없음' : '조회중',
            '차량명(기초자료)': v.master?.BASE_CAR_NM || '-',
            '영문명': v.master?.BASE_CAR_NM_ENG || '-',
            '브랜드(인식)': v.current.brand || '-',
            '브랜드(기초)': v.master?.BASE_CAR_MAKER || '-',
            '연식(인식)': v.current.modelYear || '-',
            '연식(기초)': v.master?.BASE_CAR_YEAR || '-',
            '배기량(인식)': v.current.displacement || '-',
            '배기량(기초)': v.master?.BASE_CAR_CC || '-',
            '중량(인식)': v.current.totalWeight || '-',
            '중량(기초)': v.master?.BASE_CAR_WEIGHT || '-',
            'CBM(인식)': v.current.cbm || '-',
            'CBM(기초)': v.master?.BASE_CAR_CBM || '-',
            '차대번호': v.current.vin || '-',
            '차량번호': v.current.plateNumber || '-'
          });
        }
        return baseData;
      });

      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, '검증결과');
    }

    XLSX.writeFile(wb, `검증결과_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // 필터링된 차량 목록
  const filteredVehicles = showOnlyMismatch ? vehicles.filter(v => v.mappingStatus !== 'exact') : vehicles;

  // 🎯 첫 번째 행 자동 선택 (뷰어에 bbox 표시)
  React.useEffect(() => {
    if (filteredVehicles.length > 0 && !selectedVehicleId) {
      const firstVehicle = filteredVehicles[0];
      console.log('📌 [BatchGrid] 첫 번째 행 자동 선택:', firstVehicle.id);
      setSelectedVehicleId(firstVehicle.id);
    }
  }, [filteredVehicles, selectedVehicleId]);

  // ⭐ 통합 컬럼 생성 (outputOrder 기준 정렬) - singleFields + tableFields + computedFields
  const unifiedColumns = React.useMemo(() => {
    const selectedTemplate = selectedTemplateId ? templates[selectedTemplateId] : null;
    if (!selectedTemplate) {
      console.log('📋 [UnifiedColumns] 템플릿 없음, selectedTemplateId:', selectedTemplateId);
      return [];
    }

    console.log('📋 [UnifiedColumns] 템플릿 구조:', {
      name: selectedTemplate.name,
      singleFields: selectedTemplate.singleFields?.length,
      fields: selectedTemplate.fields?.length,
      tableFields: selectedTemplate.tableFields?.length,
      computedFields: selectedTemplate.computedFields?.length
    });

    const allColumns = [];

    // 단일 필드
    const singleFieldsSource = selectedTemplate.singleFields || selectedTemplate.fields || [];
    console.log('📋 [UnifiedColumns] singleFields 개수:', singleFieldsSource.length);
    singleFieldsSource.forEach((f, idx) => {
      const field = typeof f === 'string' ? { key: f, displayName: f } : f;
      // ⭐ outputOrder가 0이면 제외, undefined면 인덱스 기반 기본 순서 할당
      const order = field.outputOrder !== undefined ? field.outputOrder : (idx + 1);
      console.log('📋 [UnifiedColumns] 단일필드:', field.key, 'outputOrder:', field.outputOrder, '=> 사용순서:', order, 'displayName:', field.displayName);

      // outputOrder가 0이면 제외, 그 외 모두 포함
      if (order > 0) {
        allColumns.push({
          key: field.key,
          displayName: field.displayName || field.key,
          order: order,
          fieldType: 'single'
        });
      }
    });

    // 테이블 컬럼
    console.log('📋 [UnifiedColumns] tableFields 구조:', selectedTemplate.tableFields);
    let tableColIdx = 0;
    const singleFieldCount = allColumns.filter(c => c.fieldType === 'single').length;
    (selectedTemplate.tableFields || []).forEach(table => {
      console.log('📋 [UnifiedColumns] 테이블:', table.tableName, 'columns:', table.columns?.length);
      (table.columns || []).forEach((col, idx) => {
        // ⭐ outputOrder가 없으면 단일필드 수 + 인덱스 기반 자동 순서 할당
        const order = col.outputOrder || (singleFieldCount + 100 + tableColIdx + 1);
        console.log('📋 [UnifiedColumns] 컬럼:', col.key, 'outputOrder:', col.outputOrder, '=> 사용순서:', order);
        tableColIdx++;

        // outputOrder가 있거나, 모든 테이블 컬럼을 포함 (outputOrder 없어도 포함)
        allColumns.push({
          key: col.key,
          displayName: col.displayName || col.key,
          order: order,
          fieldType: col.type === 'computed' ? 'table_computed' : 'table',
          tableName: table.tableName,
          formula: col.formula
        });
      });
    });

    // 합성 필드
    let computedColIdx = 0;
    const totalNonComputedCount = allColumns.length;
    (selectedTemplate.computedFields || []).forEach((cf, idx) => {
      // ⭐ outputOrder가 없으면 기존 컬럼들 뒤에 자동 배치
      const order = cf.outputOrder ?? cf.order ?? (totalNonComputedCount + 200 + idx + 1);
      console.log('📋 [UnifiedColumns] 합성필드:', cf.key, 'outputOrder:', cf.outputOrder, '=> 사용순서:', order);

      // outputOrder가 0이면 제외, 그 외 모두 포함
      if (order > 0) {
        allColumns.push({
          key: cf.key,
          displayName: cf.displayName || cf.key,
          order: order,
          fieldType: 'computed',
          formula: cf.formula
        });
        computedColIdx++;
      }
    });

    // 순서 정렬
    allColumns.sort((a, b) => a.order - b.order);
    console.log('📋 [UnifiedColumns] 최종 컬럼:', allColumns.map(c => `${c.displayName}(${c.order})`).join(', '));
    return allColumns;
  }, [selectedTemplateId, templates]);

  // ⭐ 펼침 행 생성 (테이블 아이템 개수만큼 행 생성, 단일 필드는 반복)
  const flattenedRows = React.useMemo(() => {
    const selectedTemplate = selectedTemplateId ? templates[selectedTemplateId] : null;
    if (!selectedTemplate || unifiedColumns.length === 0) return [];

    const rows = [];

    // 테이블 필드 이름 찾기 (첫 번째 테이블 사용)
    const tableFieldInfo = unifiedColumns.find(c => c.fieldType === 'table');
    const tableName = tableFieldInfo?.tableName;

    filteredVehicles.forEach(vehicle => {
      // 테이블 데이터 가져오기 (🔧 FIX: items 폴백 추가)
      let tableData = tableName ? (vehicle.current[tableName] || []) : [];
      if (tableData.length === 0 && vehicle.current?.items) {
        tableData = vehicle.current.items;
      }

      // 🔍 디버그: 테이블 데이터 확인
      if (tableName) {
        console.log('🔍 [FlattenedRows Debug]', {
          vehicleId: vehicle.id,
          tableName: tableName,
          hasTableData: !!vehicle.current[tableName],
          tableDataLength: tableData.length,
          currentKeys: Object.keys(vehicle.current || {}).slice(0, 10),
          firstTableItem: tableData[0] ? Object.keys(tableData[0]) : 'empty'
        });
      }

      const itemCount = Math.max(tableData.length, 1); // 최소 1행

      // 아이템 개수만큼 행 생성
      for (let itemIdx = 0; itemIdx < itemCount; itemIdx++) {
        const row = {
          vehicleId: vehicle.id,
          vehicle: vehicle,
          itemIndex: itemIdx,
          tableData: tableData,
          isFirstItem: itemIdx === 0,
        };
        rows.push(row);
      }
    });

    console.log('📋 [FlattenedRows] 총 행 수:', rows.length);
    return rows;
  }, [filteredVehicles, selectedTemplateId, templates, unifiedColumns]);

  // 전체검증된 차량 목록 생성
  useEffect(() => {
    const vehicleList = [];

    // 🔧 템플릿 샘플 파일 필터링 (sample_tpl_로 시작하는 파일 제외)
    const filteredFileList = fileList.filter(file => !file.name?.startsWith('sample_tpl_'));

    filteredFileList.forEach(file => {
      // 🔍 DEBUG: 데이터 소스 비교 — 1차 모델 반환값이 어디서 사라지는지 추적
      const _metaKeys = new Set(['pageResults','items','metadata','documentType','pageNumber','P','p','page',
        'boundingBoxes','labels','ocrImageSize','pngImage','extractedData','flashData','proData',
        'differences','correctedImageUrl','timestamp','source','_synthetic']);
      const _pickSingle = (obj) => {
        if (!obj || typeof obj !== 'object') return {};
        const out = {};
        Object.keys(obj).forEach(k => {
          if (_metaKeys.has(k)) return;
          const v = obj[k];
          if (!Array.isArray(v) && (v === null || typeof v !== 'object')) out[k] = v;
        });
        return out;
      };
      // 소스1: file.extractedData.pageResults[0] (배치 그리드가 사용)
      const _edPR0 = file.extractedData?.pageResults?.[0];
      // 소스2: file.pageResults[0] (오른쪽 패널이 사용)
      const _fPR0 = file.pageResults?.[0];
      // 소스3: file.extractedData 자체 (top-level 단일필드)
      const _ed = file.extractedData;
      console.log('🔎 [DATA-SOURCE]', file.name?.substring(0, 25), {
        branch: (showMergedView && file.mergedGroups?.length > 0) ? 'merged'
          : (file.extractedData?.items?.length > 0) ? 'branch2(items)'
          : (file.extractedData?.pageResults?.length > 0) ? 'branch3(prOnly)'
          : (file.pageResults?.length > 0) ? 'branch4(filePR)'
          : 'branch5(other)',
        'extractedData.pageResults[0]': _pickSingle(_edPR0),
        'file.pageResults[0]': _pickSingle(_fPR0),
        'extractedData(topLevel)': _pickSingle(_ed),
        itemsCount: file.extractedData?.items?.length || 0,
        items0Keys: file.extractedData?.items?.[0] ? Object.keys(file.extractedData.items[0]).slice(0, 8) : 'N/A',
      });

      // 📄 머지된 그룹이 있고 머지 보기 모드일 때
      if (showMergedView && file.mergedGroups && file.mergedGroups.length > 0) {
        console.log('📦 [BatchGrid] mergedGroups 사용:', file.name, file.mergedGroups.length, '그룹');

        file.mergedGroups.forEach((group, groupIdx) => {
          const mergedData = group.mergedData || {};
          const sourcePages = group.sourcePages || [];
          const firstPageNum = sourcePages.length > 0
            ? Math.min(...sourcePages.map(p => p.pageNumber))
            : 1;

          vehicleList.push({
            id: `${file.id}-merged-${groupIdx}`,
            fileId: file.id,
            fileName: file.name,
            pageNumber: firstPageNum,
            documentType: mergedData.documentType || group.sourcePages?.[0]?.docType || '문서',
            // 📄 머지 정보
            isMerged: group.type === 'merged',
            mergeMatchType: group.matchType || 'exact',  // ⭐ 매칭 타입 (exact/normalized/partial)
            matchKey: group.matchKey,
            matchValue: group.matchValue,
            sourcePages: sourcePages,
            conflicts: group.conflicts || [],
            // 현재 인식된 정보 (머지된 데이터)
            current: {
              ...mergedData
            },
            master: null,
            mappingStatus: 'pending',
            similarity: null
          });
        });
      } else if (file.extractedData?.items?.length > 0 || (file.pageResults?.length === 1 && file.pageResults[0].extractedData?.items?.length > 0)) {
        // 🚀 [Speed Mode Multi-Page Fix] extractedData.items가 있거나 pageResults[0]에 숨겨진 경우 우선 처리
        let extractedData = file.extractedData;

        // 숨겨진 데이터 추출
        if (!extractedData?.items?.length && file.pageResults?.[0]?.extractedData?.items?.length) {
          console.log('🔥 [GRID FIX] Promoting hidden items from pageResults[0]');
          extractedData = file.pageResults[0].extractedData;
        }

        const items = extractedData.items || [];

        // pageNumber별로 그룹화
        // 🔧 FIX: extractedData.pageResults[i]의 단일 필드(Invoice No., bl, Grossweight 등)를
        //         current에 병합 — 기존에는 파일 레벨 extractedData만 spread하여 단일 필드 누락
        const pages = {};

        // 🔍 DEBUG: extractedData 구조 확인
        const prSource = extractedData.pageResults || file.pageResults || [];
        console.log('🔍 [Branch2 DEBUG]', file.name, {
          extractedDataKeys: Object.keys(extractedData).slice(0, 15),
          pageResultsSource: Array.isArray(prSource) ? prSource.length : typeof prSource,
          firstPageResultKeys: prSource[0] ? Object.keys(prSource[0]).slice(0, 15) : 'N/A',
          itemsCount: items.length,
          firstItemKeys: items[0] ? Object.keys(items[0]).slice(0, 10) : 'N/A',
        });

        items.forEach((item, idx) => {
          // 🔧 pageNumber로 통일
          const pNum = item.pageNumber ?? 1;
          if (!pages[pNum]) {
            // 해당 페이지의 pageResult에서 단일 필드 가져오기
            const matchingPageResult = prSource.find(
              pr => Number(pr.pageNumber || pr.P || 1) === Number(pNum)
            ) || {};

            // 🔍 DEBUG: matchingPageResult 확인
            if (idx === 0) {
              const metaKeys = ['pageResults', 'items', 'metadata', 'documentType', 'pageNumber', 'P'];
              const singleFieldKeys = Object.keys(matchingPageResult).filter(k => !metaKeys.includes(k) && !Array.isArray(matchingPageResult[k]) && typeof matchingPageResult[k] !== 'object');
              console.log('🔍 [Branch2 matchingPageResult]', file.name, 'pNum:', pNum, {
                found: Object.keys(matchingPageResult).length > 0,
                allKeys: Object.keys(matchingPageResult).slice(0, 15),
                singleFieldKeys: singleFieldKeys.slice(0, 10),
                sampleValues: singleFieldKeys.slice(0, 5).reduce((acc, k) => { acc[k] = matchingPageResult[k]; return acc; }, {})
              });
            }

            pages[pNum] = { ...extractedData, ...matchingPageResult, items: [] };
          }
          pages[pNum].items.push(item);
        });

        // 각 페이지별로 Vehicle 생성
        Object.keys(pages).forEach(pKey => {
          const pageNum = parseInt(pKey);
          const pageData = pages[pKey];

          // 🔍 DEBUG: current에 단일 필드가 있는지 확인
          const metaKeys2 = ['pageResults', 'items', 'metadata', 'documentType', 'pageNumber', 'P'];
          const currentSingleFields = Object.keys(pageData).filter(k => !metaKeys2.includes(k) && !Array.isArray(pageData[k]) && typeof pageData[k] !== 'object');
          console.log('🔍 [Branch2 Vehicle]', file.name, 'p' + pageNum, {
            currentKeys: Object.keys(pageData).slice(0, 15),
            singleFields: currentSingleFields.slice(0, 10),
            sampleValues: currentSingleFields.slice(0, 5).reduce((acc, k) => { acc[k] = pageData[k]; return acc; }, {}),
            itemsCount: pageData.items?.length
          });

          vehicleList.push({
            id: `${file.id}-p${pageNum}`,
            fileId: file.id,
            fileName: file.name,
            pageNumber: pageNum,
            documentType: extractedData.documentType || file.documentType || '문서',
            current: { ...pageData },
            master: null,
            mappingStatus: 'pending',
            similarity: null
          });
        });

        // 🔧 속도 모드 결과: file.extractedData.pageResults에 원본 데이터
      } else if (file.extractedData?.pageResults?.length > 0) {
        console.log('🚀 [BatchGrid] 속도 모드 pageResults 사용:', file.extractedData.pageResults.length, '페이지');
        file.extractedData.pageResults.forEach((pageResult, index) => {
          // pageResult에 직접 필드가 있음 (속도 모드)
          const extractedData = pageResult;

          if (index === 0) {
            console.log('🔍 [속도 모드 DEBUG] pageResult keys:', Object.keys(pageResult).slice(0, 10));
            console.log('🔍 [속도 모드 DEBUG] sample자동차:', pageResult['자동차등록번호']);
          }

          vehicleList.push({
            id: `${file.id}-${pageResult.pageNumber || pageResult.P || index + 1}`,
            fileId: file.id,
            fileName: file.name,
            pageNumber: pageResult.pageNumber || pageResult.P || index + 1,
            documentType: file.extractedData?.documentType || '문서',
            flashData: {},
            proData: {},
            hasCrossValidation: false,
            current: { ...extractedData },
            master: null,
            mappingStatus: 'pending',
            similarity: null
          });
        });

      } else if (file.pageResults && file.pageResults.length > 0) {
        // 좌표 모드 결과: 원본 페이지별 보기
        file.pageResults.forEach((pageResult, index) => {
          // 🔧 FIX v2: extractedData가 유효한지 검증 (실제 필드가 있는지)
          const metaKeys = ['documentType', 'pageResults', 'metadata', 'items', 'pageNumber', 'P', 'page', 'boundingBoxes', 'labels', 'ocrImageSize', 'pngImage', 'extractedData', 'flashData', 'proData'];

          const isValidData = (data) => {
            if (!data || typeof data !== 'object') return false;
            const actualKeys = Object.keys(data).filter(k => !metaKeys.includes(k));
            return actualKeys.length > 0;
          };

          // 🔧 v3: 중첩된 pageResults에서 데이터 추출
          let extractedData = null;

          // Case 1: extractedData.pageResults가 있으면 해당 페이지 데이터 사용
          if (pageResult.extractedData?.pageResults?.length > 0) {
            const nestedPageData = pageResult.extractedData.pageResults.find(
              p => p.pageNumber === pageResult.pageNumber || p.P === pageResult.pageNumber
            ) || pageResult.extractedData.pageResults[index];
            if (isValidData(nestedPageData)) {
              extractedData = nestedPageData;
              console.log('✅ [BatchGrid v3] 중첩 pageResults에서 데이터 추출:', pageResult.pageNumber);
            }
          }

          // Case 2: 기존 폴백 로직
          if (!extractedData && isValidData(pageResult.extractedData)) {
            extractedData = pageResult.extractedData;
          } else if (!extractedData && isValidData(pageResult.proData)) {
            extractedData = pageResult.proData;
          } else if (!extractedData && isValidData(pageResult.flashData)) {
            extractedData = pageResult.flashData;
          } else if (!extractedData && isValidData(pageResult)) {
            extractedData = pageResult;
          } else if (!extractedData) {
            extractedData = {};
          }

          // 🔍 DEBUG v2
          if (index === 0) {
            console.log('🔍 [BatchValidationGrid DEBUG v2] extractedData:', {
              usedSource: isValidData(pageResult.extractedData) ? 'extractedData' :
                isValidData(pageResult.proData) ? 'proData' :
                  isValidData(pageResult.flashData) ? 'flashData' :
                    isValidData(pageResult) ? 'pageResult' : 'empty',
              extractedKeys: Object.keys(extractedData).slice(0, 15),
              sample자동차: extractedData['자동차등록번호'],
              sample차명: extractedData['차 명']
            });
          }

          // 🔧 교차검증용 데이터 추출
          const flashData = pageResult.flashData || {};
          const proData = pageResult.proData || {};

          // ⭐ 필터링 조건 완화: extractedData 또는 boundingBoxes가 있으면 표시
          const hasBboxes = (pageResult.boundingBoxes?.length > 0) || (pageResult.labels?.length > 0);
          if ((extractedData && Object.keys(extractedData).length > 0) || hasBboxes) {
            vehicleList.push({
              id: `${file.id}-${pageResult.pageNumber}`,
              fileId: file.id,
              fileName: file.name,
              pageNumber: pageResult.pageNumber,
              documentType: extractedData.documentType || pageResult.documentType || '문서',
              // 🔧 교차검증용 원본 데이터 저장
              flashData: flashData,
              proData: proData,
              hasCrossValidation: Object.keys(flashData).length > 0 && Object.keys(proData).length > 0,
              // 현재 인식된 정보
              current: {
                model: extractedData.model || '',
                brand: extractedData.brand || '',
                modelYear: extractedData.modelYear || '',
                displacement: extractedData.displacement || '',
                totalWeight: extractedData.totalWeight || '',
                cbm: extractedData.cbm || '',
                plateNumber: extractedData.plateNumber || '',
                vin: extractedData.vin || '',
                // 동적 필드 추가
                ...extractedData
              },
              // 기초자료 정보 (아직 조회 안 함)
              master: null,
              mappingStatus: 'pending', // 'pending', 'exact', 'similar', 'not_found'
              similarity: null
            });
          }
        });
      } else if (file.extractedData || file.proData || file.flashData || file.proResult?.data || file.flashResult?.data || file.boundingBoxes?.length > 0) {
        // 단일 이미지 처리 (추출 성공 또는 boundingBoxes 있음)
        const extractedData = file.extractedData || file.proData || file.flashData || file.proResult?.data || file.flashResult?.data || {};

        vehicleList.push({
          id: `${file.id}-single`,
          fileId: file.id,
          fileName: file.name,
          pageNumber: 1,
          documentType: extractedData.documentType || file.documentType || '문서',
          current: {
            model: extractedData.model || '',
            brand: extractedData.brand || '',
            modelYear: extractedData.modelYear || '',
            displacement: extractedData.displacement || '',
            totalWeight: extractedData.totalWeight || '',
            cbm: extractedData.cbm || '',
            plateNumber: extractedData.plateNumber || '',
            vin: extractedData.vin || '',
            // 동적 필드 추가
            ...extractedData
          },
          master: null,
          mappingStatus: 'pending',
          similarity: null
        });
      } else if (!file.pageResults && file.file && (file.file.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name))) {
        // ⭐ 단일 이미지 파일 (추출 실패 또는 오류 상태 포함)
        vehicleList.push({
          id: `${file.id}-single`,
          fileId: file.id,
          fileName: file.name,
          pageNumber: 1,
          documentType: file.error ? '오류' : '미추출',
          current: {},
          master: null,
          mappingStatus: file.error ? 'error' : 'pending',
          similarity: null,
          hasError: !!file.error,
          errorMessage: file.error
        });
      }
    });

    // 🔧 [POST-PROCESS] 모든 vehicle에 대해 pageResults에서 단일 필드 보강
    // Branch 2/3/4 어디를 타든 상관없이, file.pageResults에서 단일 필드를 찾아 current에 병합
    const metaSkipKeys = new Set(['pageResults', 'items', 'metadata', 'documentType', 'pageNumber', 'P', 'p', 'page',
      'boundingBoxes', 'labels', 'ocrImageSize', 'pngImage', 'extractedData', 'flashData', 'proData',
      'differences', 'correctedImageUrl', 'timestamp', 'source']);

    console.log('🔧 [POST-PROCESS] 시작:', vehicleList.length, '개 vehicle');
    vehicleList.forEach((vehicle, vIdx) => {
      const file = filteredFileList.find(f => f.id === vehicle.fileId);
      if (!file) { console.warn('🔧 [POST-PROCESS] file not found for', vehicle.fileId); return; }

      // pageResults 소스: file.extractedData.pageResults 또는 file.pageResults
      const prArray = file.extractedData?.pageResults || file.pageResults || [];
      if (prArray.length === 0) { if (vIdx < 3) console.warn('🔧 [POST-PROCESS] prArray empty for', vehicle.fileName); return; }

      // 해당 vehicle의 pageNumber에 맞는 pageResult 찾기
      const pNum = vehicle.pageNumber || 1;
      const matchPR = prArray.find(pr => Number(pr.pageNumber || pr.P || 1) === Number(pNum))
        || (prArray.length === 1 ? prArray[0] : null);
      if (!matchPR) { if (vIdx < 3) console.warn('🔧 [POST-PROCESS] matchPR not found for', vehicle.fileName, 'pNum:', pNum); return; }

      // matchPR에서 단일 필드(문자열/숫자/null)만 추출하여 current에 병합
      let enrichCount = 0;
      const enrichedKeys = [];
      Object.keys(matchPR).forEach(key => {
        if (metaSkipKeys.has(key)) return;
        const val = matchPR[key];
        if (Array.isArray(val) || (val !== null && typeof val === 'object')) return;
        // 🔧 FIX: null/undefined/빈문자열 모두 처리 — pageResult의 값으로 덮어쓰기
        const curVal = vehicle.current[key];
        if (curVal === undefined || curVal === null || curVal === '') {
          vehicle.current[key] = (val === null || val === undefined) ? '' : val;
          enrichCount++;
          enrichedKeys.push(key);
        }
      });

      // 🔧 [FALLBACK] 오른쪽 패널과 동일한 폴백: extractedData[field] || proData[field] || flashData[field]
      // PageBasedGridPanel.jsx line 3368과 동일 — 1차 모델이 빈값이면 2차 모델 값으로 보완
      const enhancedPR = (file.pageResults || []).find(
        pr => Number(pr.pageNumber || pr.P || 1) === Number(pNum)
      ) || (file.pageResults?.length === 1 ? file.pageResults[0] : null);
      if (enhancedPR) {
        const proData = enhancedPR.proData || {};
        const flashData = enhancedPR.flashData || {};
        const allKeys = new Set([
          ...Object.keys(vehicle.current),
          ...Object.keys(proData),
          ...Object.keys(flashData)
        ]);
        allKeys.forEach(key => {
          if (metaSkipKeys.has(key)) return;
          const curVal = vehicle.current[key];
          if (curVal === undefined || curVal === null || curVal === '') {
            const proVal = proData[key];
            const flashVal = flashData[key];
            const fallbackVal = (proVal !== undefined && proVal !== null && proVal !== '' && !Array.isArray(proVal) && (typeof proVal !== 'object' || proVal === null))
              ? proVal
              : (flashVal !== undefined && flashVal !== null && flashVal !== '' && !Array.isArray(flashVal) && (typeof flashVal !== 'object' || flashVal === null))
                ? flashVal : null;
            if (fallbackVal !== null) {
              vehicle.current[key] = fallbackVal;
              enrichCount++;
              enrichedKeys.push(`${key}(fb)`);
            }
          }
        });
      }

      // 🔧 항상 로그 (첫 3개 vehicle 또는 보강된 경우)
      if (vIdx < 3 || enrichCount > 0) {
        console.log('🔧 [POST-PROCESS]', vehicle.fileName, 'p' + pNum,
          `enriched: ${enrichCount}`,
          enrichCount > 0 ? ('keys: ' + enrichedKeys.join(', ')) : '',
          'Invoice:', vehicle.current['Invoice Number'] !== undefined ? `"${vehicle.current['Invoice Number']}"` : 'MISSING',
          'bl:', vehicle.current['bl'] !== undefined ? `"${vehicle.current['bl']}"` : 'MISSING'
        );
      }
    });

    setVehicles(vehicleList);

    // 🔧 자동 템플릿 선택 - 첫 번째 문서의 documentType과 일치하는 템플릿 자동 선택
    if (vehicleList.length > 0 && templates) {
      const firstDocType = vehicleList[0].documentType;
      console.log('🔍 [템플릿 자동선택] documentType:', firstDocType);
      console.log('🔍 [템플릿 자동선택] 사용 가능한 템플릿:', Object.values(templates).map(t => t.name));

      // 🔧 한글-영어 문서 타입 매핑
      const DOC_TYPE_MAPPING = {
        '쇼핑내역서': ['Shoring List', 'Packing List', '쇼핑내역서', 'Shoring'],
        '쇼링내역서': ['Shoring List', 'Packing List', '쇼링내역서', 'Shoring'],
        '패킹리스트': ['Packing List', '패킹리스트'],
        '인보이스': ['Commercial Invoice', 'Invoice', '인보이스'],
        '상업송장': ['Commercial Invoice', 'Invoice', '상업송장'],
        '사업자등록증': ['Business Registration', '사업자등록증'],
        '자동차등록증': ['Vehicle Registration', '자동차등록증', '차량등록증'],
        'B/L': ['Bill of Lading', 'B/L', 'BL'],
        '선하증권': ['Bill of Lading', 'B/L', '선하증권'],
      };

      if (firstDocType) {
        // 1. 직접 매칭 시도
        let matchingTemplate = Object.values(templates).find(t =>
          t.name === firstDocType ||
          t.name.toUpperCase().replace(/ /g, '_') === firstDocType.toUpperCase().replace(/ /g, '_')
        );

        // 2. 매핑 테이블로 매칭 시도
        if (!matchingTemplate) {
          const possibleNames = DOC_TYPE_MAPPING[firstDocType] || [];
          for (const possibleName of possibleNames) {
            matchingTemplate = Object.values(templates).find(t =>
              t.name === possibleName ||
              t.name.toUpperCase().replace(/ /g, '_') === possibleName.toUpperCase().replace(/ /g, '_')
            );
            if (matchingTemplate) break;
          }
        }

        // 3. 부분 일치 시도 (contains)
        if (!matchingTemplate) {
          matchingTemplate = Object.values(templates).find(t =>
            t.name.includes(firstDocType) || firstDocType.includes(t.name)
          );
        }

        if (matchingTemplate) {
          setInternalTemplateId(matchingTemplate.id);
          console.log(`📋 [자동 템플릿 선택] "${matchingTemplate.name}" (ID: ${matchingTemplate.id}) 템플릿 선택됨`);
        } else {
          console.warn(`⚠️ [자동 템플릿 선택] "${firstDocType}"와 일치하는 템플릿을 찾을 수 없음`);
        }
      }
    } else {
      console.log('⚠️ [자동 템플릿 선택] vehicleList 또는 templates가 비어있음:', { vehicleCount: vehicleList.length, templatesEmpty: !templates });
    }

    // 각 차량에 대해 기초자료 조회
    vehicleList.forEach(vehicle => {
      if (vehicle.current.model) {
        fetchMasterData(vehicle.id, vehicle.current.model);
      }
    });
  }, [fileList, templates, showMergedView]); // 🔧 showMergedView 추가 - 토글 시 재렌더링

  // 기초자료 조회
  const fetchMasterData = async (vehicleId, carName) => {
    if (!carName) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/vehicle-master/search/${encodeURIComponent(carName)}`);

      if (response.ok) {
        const result = await response.json();

        setVehicles(prev => prev.map(v => {
          if (v.id === vehicleId) {
            if (result.success && result.data) {
              return {
                ...v,
                master: result.data,
                mappingStatus: result.isSimilar ? 'similar' : 'exact',
                similarity: result.mapping?.similarity || (result.isSimilar ? 0.8 : 1.0)
              };
            } else {
              return {
                ...v,
                mappingStatus: 'not_found'
              };
            }
          }
          return v;
        }));
      }
    } catch (error) {
      console.error('기초자료 조회 실패:', error);
    }
  };

  // 유사도 검색
  const searchSimilarity = async (vehicle) => {
    if (!vehicle.current.model) return;

    setSearchingSimilarity(true);
    setSelectedVehicle(vehicle);

    try {
      const response = await fetch(`${API_BASE_URL}/api/vehicle-master/search-similar/${encodeURIComponent(vehicle.current.model)}?threshold=0.6`);

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSimilarMatches(result.results || []);
          setShowMappingModal(true);
        }
      }
    } catch (error) {
      console.error('유사도 검색 실패:', error);
    } finally {
      setSearchingSimilarity(false);
    }
  };

  // 매핑 확인 및 저장
  const confirmMapping = async (vehicle, masterData, similarity, matchType = 'manual') => {
    try {
      // 매핑 저장
      const mappingResponse = await fetch(`${API_BASE_URL}/api/vehicle-master/save-mapping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ocrRecognizedName: vehicle.current.model,
          masterId: masterData.BASE_CAR_NO,
          carNameKr: masterData.BASE_CAR_NM,
          carNameEn: masterData.BASE_CAR_NM_ENG,
          similarityScore: similarity,
          matchType: matchType,
          userId: 'system'
        })
      });

      if (mappingResponse.ok) {
        // 차량 정보 업데이트
        setVehicles(prev => prev.map(v => {
          if (v.id === vehicle.id) {
            return {
              ...v,
              master: masterData,
              mappingStatus: similarity === 1.0 ? 'exact' : 'similar',
              similarity: similarity
            };
          }
          return v;
        }));

        setShowMappingModal(false);
        alert('차량명 매핑이 저장되었습니다. 다음부터 자동으로 매핑됩니다.');
      }
    } catch (error) {
      console.error('매핑 저장 실패:', error);
      alert('매핑 저장에 실패했습니다.');
    }
  };

  // 기초자료 업데이트
  const updateMasterData = async (vehicle, masterId) => {
    try {
      const current = vehicle.current;
      const response = await fetch(`${API_BASE_URL}/api/vehicle-master/${masterId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carNameKr: vehicle.master?.BASE_CAR_NM || current.model,
          carNameEn: current.model || vehicle.master?.BASE_CAR_NM_ENG || '',
          brand: current.brand || vehicle.master?.BASE_CAR_MAKER || '',
          modelYear: current.modelYear || vehicle.master?.BASE_CAR_YEAR || '',
          displacement: current.displacement || vehicle.master?.BASE_CAR_CC || '',
          weight: current.totalWeight || vehicle.master?.BASE_CAR_WEIGHT || '',
          cbm: current.cbm || vehicle.master?.BASE_CAR_CBM || '',
          userId: 'system'
        })
      });

      if (response.ok) {
        alert('기초자료가 업데이트되었습니다.');
        // 기초자료 재조회
        if (vehicle.current.model) {
          await fetchMasterData(vehicle.id, vehicle.current.model);
        }
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('기초자료 업데이트 실패:', error);
      alert('기초자료 업데이트에 실패했습니다.');
    }
  };

  const getStatusBadge = (vehicle) => {
    switch (vehicle.mappingStatus) {
      case 'exact':
        return <span className="px-1 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-bold">100%</span>;
      case 'similar':
        return <span className="px-1 py-0.5 bg-gray-100 text-yellow-700 rounded text-[10px] font-bold">{Math.round(vehicle.similarity * 100)}%</span>;
      case 'not_found':
        return <span className="px-1 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">없음</span>;
      case 'error':
        return <span className="px-1 py-0.5 bg-[#FCE8E6] text-[#C5221F] rounded text-[10px] font-bold">오류</span>;
      default:
        return <span className="px-1 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px]">...</span>;
    }
  };

  // 뷰어용 파일 및 페이지 정보 가져오기
  const getViewerProps = () => {
    console.log('🔍 [getViewerProps] selectedVehicleId:', selectedVehicleId);
    if (!selectedVehicleId) return null;

    const vehicle = vehicles.find(v => v.id === selectedVehicleId);
    console.log('🔍 [getViewerProps] Found vehicle:', vehicle?.id, 'fileId:', vehicle?.fileId, 'pageNumber:', vehicle?.pageNumber);
    if (!vehicle) return null;

    const file = fileList.find(f => f.id === vehicle.fileId);
    console.log('🔍 [getViewerProps] Found file:', file?.id, 'hasPageResults:', !!file?.pageResults, 'pageResultsCount:', file?.pageResults?.length);
    if (!file) {
      console.warn('⚠️ [getViewerProps] File not found! Available fileList ids:', fileList.map(f => f.id));
      return null;
    }

    // 🎯 pageResults에서 해당 페이지의 boundingBoxes 가져오기
    let boundingBoxes = [];
    let ocrImageSize = null;
    let pngImage = null;

    if (file.pageResults && Array.isArray(file.pageResults)) {
      const pageResult = file.pageResults.find(p => p.pageNumber === vehicle.pageNumber);
      if (pageResult) {
        // 🎯 boundingBoxes 또는 labels 필드에서 bbox 가져오기
        boundingBoxes = pageResult.boundingBoxes || pageResult.labels || [];
        ocrImageSize = pageResult.ocrImageSize || { width: pageResult.imageWidth, height: pageResult.imageHeight };
        pngImage = pageResult.pngImage; // Base64 PNG 이미지
        console.log('📍 [BatchGrid] pageResult 발견:', {
          pageNumber: pageResult.pageNumber,
          bboxCount: boundingBoxes.length,
          hasLabels: !!pageResult.labels,
          hasBoundingBoxes: !!pageResult.boundingBoxes,
          ocrImageSize,
          hasPngImage: !!pngImage
        });
      }
    } else if (file.boundingBoxes || file.labels) {
      // 단일 이미지인 경우
      boundingBoxes = file.boundingBoxes;
      ocrImageSize = file.ocrImageSize || { width: file.imageWidth, height: file.imageHeight };
    }
    // 🚀 [Speed Mode] Native Viewer 우선 활성화
    const isPdf = (file.file?.type === 'application/pdf') || file.name?.endsWith('.pdf');

    // PDF일 경우 pngImage 대신 원본 PDF blob 우선 사용 (Native Viewer용)
    let previewUrl = pngImage || file.preview;
    if (isPdf && file.preview) {
      console.log('🔥 [VIEWER FIX] Force using PDF Blob for Native Viewer');
      previewUrl = file.preview;
    }

    return {
      file: file.file,
      pageNumber: vehicle.pageNumber,
      isPDF: isPdf,
      fileType: isPdf ? 'application/pdf' : (file.file?.type || 'image/jpeg'),
      isHybridMode: true, // 🚀 Native Viewer 활성화
      preview: previewUrl,
      boundingBoxes,
      ocrImageSize
    };
  };

  const viewerProps = getViewerProps();

  // 🔍 디버그: bbox 데이터 확인
  React.useEffect(() => {
    if (viewerProps) {
      console.log('🎯 [BatchGrid] viewerProps:', {
        hasBoxes: !!viewerProps.boundingBoxes?.length,
        boxCount: viewerProps.boundingBoxes?.length || 0,
        hasImageSize: !!viewerProps.ocrImageSize,
        ocrImageSize: viewerProps.ocrImageSize,
        showLabels,
        firstBox: viewerProps.boundingBoxes?.[0]
      });
    }
  }, [viewerProps, showLabels]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 헤더 - 여유롭게 (오른쪽 패널 헤더와 동일 높이) */}
      <div className="flex-shrink-0 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-3">
          {/* 디버깅용 로그 */}
          {console.log('Selected Template:', selectedTemplateId, templates[selectedTemplateId])}
          {console.log('Filtered Vehicles:', filteredVehicles)}
          <h2 className="text-sm font-semibold" style={{ color: '#0A1037' }}>
            전체검증 ({filteredVehicles.length}/{vehicles.length}건)
          </h2>

          {/* 템플릿은 왼쪽 패널에서만 표시 */}
          <label className="flex items-center gap-1.5 text-xs cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={showOnlyMismatch}
              onChange={(e) => setShowOnlyMismatch(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300"
            />
            <span className="text-gray-600">100% 미만만 표시</span>
          </label>

          {/* 📄 원본/머지 토글 버튼 */}
          <div className="flex items-center gap-1 ml-4 bg-gray-200 rounded-full p-0.5">
            <button
              onClick={() => setShowMergedView(false)}
              className={`px-2 py-0.5 text-xs rounded-full transition-all ${!showMergedView
                ? 'bg-white text-blue-700 shadow-sm font-medium'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              원본 페이지별
            </button>
            <button
              onClick={() => setShowMergedView(true)}
              className={`px-2 py-0.5 text-xs rounded-full transition-all ${showMergedView
                ? 'bg-white text-purple-700 shadow-sm font-medium'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              머지 결과
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* 🎨 엑셀 다운로드 (Google AI Studio Style - 심플) */}
          <button
            onClick={handleExcelDownload}
            className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded text-xs transition-colors"
            title="엑셀로 내보내기"
          >
            <FileSpreadsheet className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 메인 컨텐츠 (상하 분할) */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 상단: 그리드 (가변 높이) */}
        <div className="flex-1 overflow-auto">
          {useAUIGrid ? (
            /* 🆕 AUIGrid 모드 */
            <BatchAUIGrid
              gridId="batch-validation-grid"
              flattenedRows={flattenedRows}
              unifiedColumns={unifiedColumns}
              selectedVehicleId={selectedVehicleId}
              onRowSelect={(vehicleId) => {
                console.log('🔴 [AUIGrid onRowSelect] vehicleId:', vehicleId);
                setSelectedVehicleId(vehicleId);
                const vehicle = vehicles.find(v => v.id === vehicleId);
                console.log('🔴 [AUIGrid onRowSelect] Found vehicle:', vehicle?.id, 'pageNumber:', vehicle?.pageNumber);
                if (vehicle && onRowSelect) {
                  onRowSelect(vehicle.fileId, vehicle.pageNumber, vehicle);
                }
              }}
              onFieldClick={handleFieldClick}
              templates={templates}
              selectedTemplateId={selectedTemplateId}
              showMergedView={showMergedView}
            />
          ) : (
            /* 기존 HTML 테이블 모드 */
            <table className="w-full border-collapse text-xs">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  {selectedTemplateId && templates[selectedTemplateId] && unifiedColumns.length > 0 ? (
                    <>
                      <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-300 whitespace-nowrap">파일명</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-8">P</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-16" title="원본 페이지">원본</th>
                      {unifiedColumns.map((col, index) => (
                        <th
                          key={index}
                          className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-300 whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px]"
                          title={col.displayName}
                        >
                          {col.displayName.length > 8 ? col.displayName.substring(0, 8) + '...' : col.displayName}
                        </th>
                      ))}
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-12">작업</th>
                    </>
                  ) : (
                    <>
                      <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-300 whitespace-nowrap">파일명</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-8">P</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-300 whitespace-nowrap">차량명</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-16">매핑</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-300 whitespace-nowrap">기초명</th>
                      <th className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-300 w-14">브랜드</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-10">연식</th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600 border-b border-gray-300 w-14">배기량</th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600 border-b border-gray-300 w-14">중량</th>
                      <th className="px-2 py-1 text-right font-medium text-gray-600 border-b border-gray-300 w-10">CBM</th>
                      <th className="px-2 py-1 text-center font-medium text-gray-600 border-b border-gray-300 w-12">작업</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {selectedTemplateId && templates[selectedTemplateId] && unifiedColumns.length > 0 ? (
                  // ⭐ 템플릿 모드: flattenedRows 사용 (테이블 아이템별 행)
                  flattenedRows.map((row, rowIdx) => {
                    const { vehicle, itemIndex, tableData } = row;

                    // 대소문자 무시 키 매칭 헬퍼
                    const findValue = (data, searchKey) => {
                      if (!data || typeof data !== 'object') return '';
                      if (data[searchKey] !== undefined) return data[searchKey] || '';
                      const lowerKey = searchKey.toLowerCase();
                      const matchedKey = Object.keys(data).find(k => k.toLowerCase() === lowerKey);
                      return matchedKey ? (data[matchedKey] || '') : '';
                    };

                    return (
                      <tr
                        key={`row-${vehicle.id}-${itemIndex}`}
                        onClick={() => {
                          setSelectedVehicleId(vehicle.id);
                          // ⭐ 부모 컴포넌트에 선택 정보 전달
                          if (onRowSelect) {
                            onRowSelect(vehicle.fileId, vehicle.pageNumber, vehicle);
                          }
                        }}
                        className={`cursor-pointer hover:bg-blue-50 border-b border-gray-100 transition-colors ${selectedVehicleId === vehicle.id ? 'bg-gray-100' : ''}`}
                      >
                        <td className="px-2 py-1 text-gray-900 truncate max-w-[120px]" title={vehicle.fileName}>{vehicle.fileName}</td>
                        <td className="px-2 py-1 text-center text-gray-900">{vehicle.pageNumber}</td>
                        {/* 📄 원본 페이지 (머지된 경우 페이지 목록 + 매칭 타입 표시) */}
                        <td className="px-2 py-1 text-center text-xs">
                          {vehicle.isMerged && vehicle.sourcePages?.length > 1 ? (
                            <div className="flex items-center justify-center gap-1">
                              <span
                                className="px-1 py-0.5 bg-purple-100 text-purple-700 rounded cursor-pointer hover:bg-purple-200"
                                title={`원본: ${vehicle.sourcePages.map(p => `P${p.pageNumber}(${p.docType})`).join(', ')}`}
                              >
                                {vehicle.sourcePages.map(p => `P${p.pageNumber}`).join(',')}
                              </span>
                              {/* ⭐ 매칭 타입 색상 표시 */}
                              {vehicle.mergeMatchType === 'partial' && (
                                <span
                                  className="px-1 py-0.5 bg-orange-100 text-orange-700 rounded text-[9px] font-bold cursor-help"
                                  title="부분 일치: 값이 정확히 100% 일치하지 않습니다. 확인이 필요합니다."
                                >
                                  ⚠ 부분
                                </span>
                              )}
                              {vehicle.mergeMatchType === 'normalized' && (
                                <span
                                  className="px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] cursor-help"
                                  title="정규화 일치: 대소문자, 공백, 특수문자 차이 무시하고 연결됨"
                                >
                                  ~
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        {unifiedColumns.map((col, colIdx) => {
                          let cellValue = '-';
                          let cellClassName = "px-2 py-1 truncate max-w-[150px] ";
                          let hasDifference = false;

                          // 🔧 교차검증 편차 확인 함수
                          const checkDifference = (key) => {
                            if (!vehicle.hasCrossValidation) return false;
                            const flashVal = String(findValue(vehicle.flashData, key) || '').trim();
                            const proVal = String(findValue(vehicle.proData, key) || '').trim();
                            // 둘 다 비어있으면 차이 없음
                            if (!flashVal && !proVal) return false;
                            return flashVal !== proVal;
                          };

                          if (col.fieldType === 'single') {
                            // 단일 필드: 모든 행에 동일한 값
                            cellValue = findValue(vehicle.current, col.key) || '-';
                            // 🔍 DEBUG: 값 추적
                            if (colIdx === 0 && rowIdx === 0) {
                              console.log('🔍 [CellValue Debug]', {
                                colKey: col.key,
                                currentKeys: Object.keys(vehicle.current || {}).slice(0, 10),
                                foundValue: cellValue,
                                rawValue: vehicle.current?.[col.key]
                              });
                            }
                            hasDifference = checkDifference(col.key);
                            cellClassName += hasDifference ? "text-amber-800 bg-gray-100 font-medium" : "text-gray-900";
                          } else if (col.fieldType === 'table') {
                            // 테이블 필드: 해당 아이템의 값
                            const item = tableData[itemIndex];
                            cellValue = item ? (findValue(item, col.key) || '-') : '-';
                            // 테이블 항목 교차검증 (복잡도로 인해 기본 스타일 유지)
                            cellClassName += "text-gray-700 bg-blue-50";
                          } else if (col.fieldType === 'table_computed') {
                            // 🔧 테이블 내부 합성 필드
                            cellClassName += "text-blue-800 bg-blue-50 font-medium";
                            const item = tableData[itemIndex];

                            if (item && col.formula) {
                              let computed = col.formula;
                              const pattern = /\{([^}]+)\}/g;
                              let hasReplacement = false;

                              computed = computed.replace(pattern, (_, key) => {
                                hasReplacement = true;
                                const valKey = Object.keys(item).find(k => k.toLowerCase() === key.toLowerCase());
                                let val = valKey ? item[valKey] : '';
                                if (typeof val === 'string' && /^-?[\d,]+(\.\d+)?$/.test(val)) return val.replace(/,/g, '');
                                return val || 0;
                              });

                              if (hasReplacement && /^[\d+\-*/.()\s]+$/.test(computed)) {
                                try {
                                  // eslint-disable-next-line no-eval
                                  cellValue = String(eval(computed));
                                  // 숫자인 경우 포맷팅
                                  if (!isNaN(parseFloat(cellValue))) {
                                    cellValue = parseFloat(cellValue).toLocaleString('ko-KR');
                                    cellClassName += " text-right";
                                  }
                                } catch (e) {
                                  cellValue = computed;
                                }
                              } else {
                                let stringComputed = col.formula;
                                stringComputed = stringComputed.replace(pattern, (_, key) => {
                                  const valKey = Object.keys(item).find(k => k.toLowerCase() === key.toLowerCase());
                                  return valKey ? item[valKey] : '';
                                });
                                cellValue = stringComputed;
                              }
                            } else {
                              cellValue = '-';
                            }
                          } else if (col.fieldType === 'computed') {
                            // 합성 필드: 포뮬러 계산
                            cellClassName += "text-amber-800 bg-gray-50 font-medium";
                            let computedValue = col.formula || '';

                            // ⭐ 포뮬러에서 모든 {key} 패턴 추출
                            const pattern = /\{([^}]+)\}/g;
                            let match;
                            while ((match = pattern.exec(col.formula || '')) !== null) {
                              const formulaKey = match[1]; // {model} -> model

                              // 단일 필드에서 찾기 (대소문자 무시)
                              const singleField = unifiedColumns.find(c =>
                                c.fieldType === 'single' && c.key.toLowerCase() === formulaKey.toLowerCase()
                              );
                              if (singleField) {
                                const val = findValue(vehicle.current, singleField.key) || '';
                                computedValue = computedValue.replace(new RegExp(`\\{${formulaKey}\\}`, 'gi'), val);
                                continue;
                              }

                              // 테이블 필드에서 찾기 (대소문자 무시)
                              const tableField = unifiedColumns.find(c =>
                                c.fieldType === 'table' && c.key.toLowerCase() === formulaKey.toLowerCase()
                              );
                              if (tableField) {
                                const item = tableData[itemIndex];
                                const val = item ? (findValue(item, tableField.key) || '') : '';
                                computedValue = computedValue.replace(new RegExp(`\\{${formulaKey}\\}`, 'gi'), val);
                              }
                            }

                            cellValue = computedValue || '-';
                          }

                          // 🔧 정렬 결정: 숫자는 오른쪽, 문자열은 왼쪽
                          const isNumericValue = cellValue !== '-' &&
                            typeof cellValue === 'string' &&
                            /^-?[\d,]+(\.\d+)?$/.test(cellValue.replace(/,/g, ''));

                          const isNumericField = ['quantity', 'qty', 'amount', 'price', 'unitprice', 'unit_price',
                            'net weight', 'netweight', 'weight', '수량', '단가', '금액', '중량', '순중량']
                            .some(nf => col.key.toLowerCase().includes(nf));

                          if (isNumericValue || isNumericField || col.type === 'number' || col.type === 'integer') {
                            cellClassName += " text-right";
                          } else {
                            cellClassName += " text-left";
                          }

                          return (
                            <td
                              key={`cell-${colIdx}`}
                              className={`${cellClassName} cursor-pointer hover:ring-2 hover:ring-blue-300`}
                              title={hasDifference ? `⚠️ 교차검증 편차\nFlash: ${findValue(vehicle.flashData, col.key) || '-'}\nPro: ${findValue(vehicle.proData, col.key) || '-'}` : String(cellValue)}
                              onClick={(e) => {
                                e.stopPropagation(); // 행 선택 이벤트 방지
                                handleFieldClick(col.key, cellValue);
                              }}
                            >
                              {cellValue}
                            </td>
                          );
                        })}
                        <td className="px-2 py-1 text-center">
                          <span className="text-gray-400">-</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  // 기본 모드: 차량 정보 표시
                  filteredVehicles.map((vehicle) => (
                    <tr
                      key={vehicle.id}
                      onClick={() => {
                        setSelectedVehicleId(vehicle.id);
                        // ⭐ 부모 컴포넌트에 선택 정보 전달
                        if (onRowSelect) {
                          onRowSelect(vehicle.fileId, vehicle.pageNumber, vehicle);
                        }
                      }}
                      className={`cursor-pointer hover:bg-blue-50 border-b border-gray-100 transition-colors ${selectedVehicleId === vehicle.id ? 'bg-gray-100' : ''}`}
                    >
                      <td className="px-2 py-1 text-gray-900 truncate max-w-[120px]" title={vehicle.fileName}>{vehicle.fileName}</td>
                      <td className="px-2 py-1 text-center text-gray-900">{vehicle.pageNumber}</td>
                      <td className="px-2 py-1 text-gray-900 font-medium truncate max-w-[100px]" title={vehicle.current.model}>{vehicle.current.model || '-'}</td>
                      <td className="px-2 py-1 text-center">{getStatusBadge(vehicle)}</td>
                      <td className="px-2 py-1 text-gray-900 truncate max-w-[100px]" title={vehicle.master?.BASE_CAR_NM}>
                        {vehicle.master?.BASE_CAR_NM || '-'}
                      </td>
                      <td className="px-2 py-1">
                        <span className={vehicle.current.brand && vehicle.master?.BASE_CAR_MAKER && vehicle.current.brand !== vehicle.master.BASE_CAR_MAKER ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {vehicle.current.brand || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={vehicle.current.modelYear && vehicle.master?.BASE_CAR_YEAR && vehicle.current.modelYear !== vehicle.master.BASE_CAR_YEAR ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {vehicle.current.modelYear || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-right">
                        <span className={vehicle.current.displacement && vehicle.master?.BASE_CAR_CC && vehicle.current.displacement !== vehicle.master.BASE_CAR_CC.toString() ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {vehicle.current.displacement ? parseInt(vehicle.current.displacement).toLocaleString('ko-KR') : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-right">
                        <span className={vehicle.current.totalWeight && vehicle.master?.BASE_CAR_WEIGHT && vehicle.current.totalWeight !== vehicle.master.BASE_CAR_WEIGHT.toString() ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {vehicle.current.totalWeight ? parseInt(vehicle.current.totalWeight).toLocaleString('ko-KR') : '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-right">
                        <span className={vehicle.current.cbm && vehicle.master?.BASE_CAR_CBM && vehicle.current.cbm !== vehicle.master.BASE_CAR_CBM.toString() ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {vehicle.current.cbm || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <div className="flex items-center justify-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                          {vehicle.mappingStatus === 'not_found' && (
                            <button
                              onClick={() => searchSimilarity(vehicle)}
                              className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                              title="유사도 검색"
                            >
                              <Search className="w-3 h-3" />
                            </button>
                          )}
                          {vehicle.mappingStatus === 'similar' && (
                            <button
                              onClick={() => confirmMapping(vehicle, vehicle.master, vehicle.similarity, 'similar')}
                              className="px-1.5 py-0.5 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                              title="매핑 확인"
                            >
                              확인
                            </button>
                          )}
                          {vehicle.master && (
                            <button
                              onClick={() => updateMasterData(vehicle, vehicle.master.master_id)}
                              className="p-1 bg-green-600 text-white rounded hover:bg-green-700"
                              title="기초자료 업데이트"
                            >
                              <Save className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* 🎨 리사이즈 핸들 (Google AI Studio Style) */}
        <div
          className="h-[6px] bg-gradient-to-b from-gray-100 via-gray-200 to-gray-100 cursor-row-resize flex items-center justify-center hover:from-blue-50 hover:via-blue-100 hover:to-blue-50 transition-all z-10 select-none"
          onMouseDown={handleMouseDown}
          title="드래그하여 높이 조절"
        >
          <div className="w-10 h-[3px] bg-gray-500 rounded-full opacity-60 hover:opacity-100" />
        </div>

        {/* 하단: PDF 뷰어 (가변 높이) */}
        <div style={{ height: `${bottomHeight}px` }} className="bg-gray-100 flex flex-col">
          {/* 헤더 바 제거 - 툴바가 OCRLabelingModal에서 표시됨 */}
          <div className="flex-1 relative overflow-hidden bg-gray-200">
            {viewerProps ? (
              /* 🚀 [Speed Mode] 일관된 PDF 뷰어 (문서인식 화면과 동일) */
              viewerProps.isPDF ? (
                <div className="w-full h-full" style={{ position: 'relative' }}>
                  <ReactPDFViewer
                    file={viewerProps.file}
                    pageNumber={viewerProps.pageNumber || 1}
                    onLoadSuccess={(info) => {
                      console.log('📘 [BatchGrid ReactPDFViewer] 로드 완료:', info.numPages, '페이지');
                    }}
                    highlightText={highlightedFieldKey}
                  />
                </div>
              ) : (
                /* 이미지인 경우 기존 OCRLabelingModal 사용 */
                <OCRLabelingModal
                  key={viewerProps.preview}
                  isEmbedded={true}
                  disableAutoLabel={true}
                  imageUrl={viewerProps.preview}
                  initialBoundingBoxes={viewerProps.boundingBoxes || []}
                  ocrImageSize={viewerProps.ocrImageSize}
                  templateName="문서 뷰어"
                  apiBaseUrl={API_BASE_URL}
                  highlightedFieldKey={highlightedFieldKey}
                  onClose={() => { }}
                  onSave={() => { }}
                />
              )
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-1 text-sm">
                <Search className="w-6 h-6 opacity-50" />
                <p>목록에서 항목을 선택하세요</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 유사도 매칭 모달 */}
      {
        showMappingModal && selectedVehicle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-auto">
              {/* ... Existing Modal Content ... */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold" style={{ color: '#0A1037' }}>
                  유사 차량 검색 결과: "{selectedVehicle.current.model}"
                </h3>
                <button
                  onClick={() => {
                    setShowMappingModal(false);
                    setSelectedVehicle(null);
                    setSimilarMatches([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* ... rest of existing modal content ... */}
            </div>
          </div>
        )
      }

      {/* 🚀 [Upstage Benchmark] Data Viewer Modal for Nested Tables */}
      {
        viewDataModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
              <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">Table Data Viewer</h3>
                  <p className="text-xs text-gray-500">Field: <span className="font-mono text-blue-600 font-semibold">{viewDataModal.title}</span></p>
                </div>
                <button onClick={() => setViewDataModal(null)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-auto p-0">
                {Array.isArray(viewDataModal.data) && viewDataModal.data.length > 0 ? (
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-gray-50 sticky top-0 shadow-sm z-10">
                      <tr>
                        <th className="px-3 py-2 text-center border-b font-medium text-gray-500 w-12">#</th>
                        {Object.keys(viewDataModal.data[0]).map((key, i) => (
                          <th key={i} className="px-3 py-2 text-left border-b font-semibold text-gray-700 uppercase tracking-wide text-xs">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {viewDataModal.data.map((row, idx) => (
                        <tr key={idx} className="hover:bg-blue-50 border-b border-gray-100 last:border-0">
                          <td className="px-3 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                          {Object.values(row).map((val, vIdx) => (
                            <td key={vIdx} className="px-3 py-2 text-gray-800 truncate max-w-[200px]" title={String(val)}>
                              {val !== null && val !== undefined ? String(val) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-10 flex flex-col items-center justify-center text-gray-400">
                    <p>No data available in this table.</p>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 rounded-b-lg">
                <button
                  onClick={() => {
                    // Simple CSV Export for this table
                    if (!viewDataModal.data || viewDataModal.data.length === 0) return;
                    const ws = XLSX.utils.json_to_sheet(viewDataModal.data);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, "TableData");
                    XLSX.writeFile(wb, `${viewDataModal.title}_export.xlsx`);
                  }}
                  className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded hover:bg-green-700 flex items-center gap-1"
                >
                  <Download className="w-3.5 h-3.5" /> Export Excel
                </button>
                <button onClick={() => setViewDataModal(null)} className="px-3 py-1.5 bg-gray-200 text-gray-600 text-xs font-bold rounded hover:bg-gray-300">
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default BatchValidationGrid;

